import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setStatus('success');
      return;
    }
    if (!email.trim()) return;

    setStatus('loading');
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      await fetch('https://readdy.ai/api/form/d90je4c6ci2plld1oe30', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      setStatus('success');
      setEmail('');
    } catch {
      setStatus('error');
    }
  };

  return (
    <footer className="bg-foreground-950 rounded-2xl mx-4 md:mx-6 mb-6 overflow-hidden">
      <div className="relative px-6 md:px-12 py-14 md:py-16">
        <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
          <span className="absolute bottom-4 left-1/2 -translate-x-1/2 text-[12vw] md:text-[8vw] font-heading font-bold text-foreground-800/10 whitespace-nowrap leading-none">
            VIGORBUY
          </span>
        </div>

        <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          <div>
            <span className="font-heading text-2xl font-semibold text-background-50">vigorbuy</span>
            <p className="mt-4 text-sm text-foreground-300 leading-relaxed max-w-xs">
              The largest product discovery hub. Helping shoppers find, compare, and discover products faster since 2024.
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-background-50 mb-4">Navigate</h4>
            <div className="flex flex-col gap-2.5">
              <a href="#about" className="text-sm text-foreground-300 hover:text-background-50 transition-colors duration-200">About</a>
              <a href="#categories" className="text-sm text-foreground-300 hover:text-background-50 transition-colors duration-200">Categories</a>
              <a href="#guides" className="text-sm text-foreground-300 hover:text-background-50 transition-colors duration-200">Guides</a>
              <a href="#safety" className="text-sm text-foreground-300 hover:text-background-50 transition-colors duration-200">Safety</a>
              <Link to="/article/vigorbuy-spreadsheet-guide" className="text-sm text-foreground-300 hover:text-background-50 transition-colors duration-200">Beginner Guide</Link>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-background-50 mb-4">Resources</h4>
            <div className="flex flex-col gap-2.5">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-300 hover:text-background-50 transition-colors duration-200">Vigorbuy Sheet</a>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-300 hover:text-background-50 transition-colors duration-200">Shop Now</a>
              <Link to="/article/vigorbuy-spreadsheet-guide" className="text-sm text-foreground-300 hover:text-background-50 transition-colors duration-200">Spreadsheet Guide</Link>
              <span className="text-sm text-foreground-300">Privacy Policy</span>
              <span className="text-sm text-foreground-300">Terms of Service</span>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-background-50 mb-4">Stay Updated</h4>
            <p className="text-sm text-foreground-300 mb-4 leading-relaxed">
              Get the latest product discoveries and spreadsheet updates.
            </p>
            <form
              onSubmit={handleSubmit}
              data-readdy-form=""
              className="flex flex-col gap-2"
            >
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="honeypot-field"
              />
              <div className="flex">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="flex-1 px-4 py-2.5 bg-foreground-800/50 border border-foreground-700/50 rounded-l-lg text-sm text-background-50 placeholder:text-foreground-400 outline-none focus:border-primary-500 transition-colors duration-200"
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="px-4 py-2.5 bg-primary-500 text-background-50 rounded-r-lg hover:bg-primary-600 transition-all duration-200 cursor-pointer disabled:opacity-60 whitespace-nowrap"
                >
                  {status === 'loading' ? (
                    <i className="ri-loader-4-line animate-spin"></i>
                  ) : (
                    <i className="ri-arrow-right-line text-lg"></i>
                  )}
                </button>
              </div>
              {status === 'success' && (
                <p className="text-xs text-emerald-400 mt-1">Thanks for subscribing!</p>
              )}
              {status === 'error' && (
                <p className="text-xs text-red-400 mt-1">Something went wrong. Try again.</p>
              )}
            </form>
          </div>
        </div>

        <div className="relative mt-12 pt-6 border-t border-foreground-800/40 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-foreground-400">
            &copy; {new Date().getFullYear()} VigorBuy.courses. All rights reserved.
          </p>
          <p className="text-xs text-foreground-500">
            Not affiliated with any spreadsheet brand. Product discovery resource.
          </p>
        </div>
      </div>
    </footer>
  );
}